import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LogOut, Plus, Pencil, Eye, Store, Clock3, CheckCircle2, MessageCircleQuestion, ImagePlus, X, Star, MessageSquare } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { Modal, StatusBadge, Field, inputCls, thCls, tdCls } from "../admin/ui";
import { useLang } from "../lib/i18n";

const DAYS_ORDER = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const DAYS_SUN_FIRST = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const HOUR_OPTS = Array.from({ length: 12 }, (_, i) => String(i + 1));
const MIN_OPTS = ["00", "15", "30", "45"];
const AMPM_OPTS = ["AM", "PM"];
const DEFAULT_SLOT = { closed: false, oh: "9", om: "00", oap: "AM", ch: "6", cm: "00", cap: "PM" };

function expandDayKey(key) {
  const k = key.trim();
  const range = k.match(/^([A-Za-z]{3})\s*-\s*([A-Za-z]{3})$/);
  if (range) {
    const a = DAYS_SUN_FIRST.indexOf(range[1]);
    const b = DAYS_SUN_FIRST.indexOf(range[2]);
    if (a === -1 || b === -1) return [];
    const out = [];
    let i = a;
    for (let g = 0; g < 8; g++) {
      out.push(DAYS_SUN_FIRST[i]);
      if (i === b) break;
      i = (i + 1) % 7;
    }
    return out;
  }
  const single = k.slice(0, 3);
  return DAYS_SUN_FIRST.includes(single) ? [single] : [];
}

function parseHoursToStruct(hours) {
  const struct = {};
  for (const [key, val] of Object.entries(hours || {})) {
    const days = expandDayKey(key);
    if (!days.length) continue;
    let slot = { ...DEFAULT_SLOT };
    if (/closed/i.test(val)) {
      slot = { ...DEFAULT_SLOT, closed: true };
    } else {
      const m = val.match(/(\d{1,2})(?::(\d{2}))?\s*(AM|PM)\s*[-–]\s*(\d{1,2})(?::(\d{2}))?\s*(AM|PM)/i);
      if (m) {
        slot = { closed: false, oh: String(+m[1]), om: m[2] || "00", oap: m[3].toUpperCase(), ch: String(+m[4]), cm: m[5] || "00", cap: m[6].toUpperCase() };
      }
    }
    days.forEach((d) => { struct[d] = { ...slot }; });
  }
  return struct;
}

function structToHours(struct) {
  const out = {};
  DAYS_ORDER.forEach((d) => {
    const s = struct[d];
    if (!s) return;
    out[d] = s.closed ? "Closed" : `${s.oh}:${s.om} ${s.oap} - ${s.ch}:${s.cm} ${s.cap}`;
  });
  return out;
}

function HoursEditor({ value, onChange }) {
  const { t } = useLang();
  const selCls = "px-2 py-2 rounded-lg bg-sand/60 text-sm outline-none border border-transparent focus:border-gold";
  const setDay = (d, patch) => onChange({ ...value, [d]: { ...value[d], ...patch } });
  return (
    <div className="space-y-2" data-testid="hours-editor">
      {DAYS_ORDER.map((d) => {
        const slot = value[d];
        if (!slot) {
          return (
            <div key={d} className="flex items-center justify-between gap-2 py-1">
              <span className="text-sm font-semibold text-stone2 w-28">{t(`own.day.${d}`)}</span>
              <button type="button" onClick={() => onChange({ ...value, [d]: { ...DEFAULT_SLOT } })} className="text-xs font-semibold text-ruby hover:underline" data-testid={`hours-add-${d.toLowerCase()}`}>
                + {t("own.hours.set")}
              </button>
            </div>
          );
        }
        return (
          <div key={d} className="flex flex-wrap items-center gap-2" data-testid={`hours-row-${d.toLowerCase()}`}>
            <span className="text-sm font-semibold text-ink w-28">{t(`own.day.${d}`)}</span>
            {!slot.closed && (
              <>
                <select value={slot.oh} onChange={(e) => setDay(d, { oh: e.target.value })} className={selCls} data-testid={`hours-open-hour-${d.toLowerCase()}`}>
                  {HOUR_OPTS.map((h) => <option key={h} value={h}>{h}</option>)}
                </select>
                <select value={slot.om} onChange={(e) => setDay(d, { om: e.target.value })} className={selCls} data-testid={`hours-open-min-${d.toLowerCase()}`}>
                  {MIN_OPTS.map((m) => <option key={m} value={m}>{m}</option>)}
                </select>
                <select value={slot.oap} onChange={(e) => setDay(d, { oap: e.target.value })} className={selCls} data-testid={`hours-open-ampm-${d.toLowerCase()}`}>
                  {AMPM_OPTS.map((a) => <option key={a} value={a}>{a}</option>)}
                </select>
                <span className="text-stone2 text-sm">–</span>
                <select value={slot.ch} onChange={(e) => setDay(d, { ch: e.target.value })} className={selCls} data-testid={`hours-close-hour-${d.toLowerCase()}`}>
                  {HOUR_OPTS.map((h) => <option key={h} value={h}>{h}</option>)}
                </select>
                <select value={slot.cm} onChange={(e) => setDay(d, { cm: e.target.value })} className={selCls} data-testid={`hours-close-min-${d.toLowerCase()}`}>
                  {MIN_OPTS.map((m) => <option key={m} value={m}>{m}</option>)}
                </select>
                <select value={slot.cap} onChange={(e) => setDay(d, { cap: e.target.value })} className={selCls} data-testid={`hours-close-ampm-${d.toLowerCase()}`}>
                  {AMPM_OPTS.map((a) => <option key={a} value={a}>{a}</option>)}
                </select>
              </>
            )}
            <label className="flex items-center gap-1.5 text-xs text-clay ml-auto cursor-pointer">
              <input type="checkbox" checked={slot.closed} onChange={(e) => setDay(d, { closed: e.target.checked })} className="w-3.5 h-3.5 accent-[#8B1A24]" data-testid={`hours-closed-${d.toLowerCase()}`} />
              {t("own.hours.closed")}
            </label>
            <button
              type="button"
              onClick={() => { const next = { ...value }; delete next[d]; onChange(next); }}
              className="p-1 text-stone2 hover:text-ruby"
              data-testid={`hours-remove-${d.toLowerCase()}`}
              aria-label={t("own.hours.remove")}
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        );
      })}
    </div>
  );
}

const EMPTY = {
  name: "", category: "", description: "", price: "", event_date: "", phone: "", email: "", website: "", logo: "",
  address: { line1: "", city: "", state: "", postal: "", country: "US" },
  images: "", languages: "", hours_struct: {}, actions: {},
};

export default function OwnerApp() {
  const [me, setMe] = useState(null);
  const [listings, setListings] = useState(null);
  const [analytics, setAnalytics] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [replies, setReplies] = useState({});
  const [uploading, setUploading] = useState(false);
  const [answers, setAnswers] = useState({});
  const [autoPublish, setAutoPublish] = useState(false);
  const [categories, setCategories] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();
  const { t, tOr, lang, setLang } = useLang();

  const load = () => {
    api.get("/owner/listings").then((r) => setListings(r.data || [])).catch(() => setListings([]));
    api.get("/owner/analytics").then((r) => setAnalytics(r.data || [])).catch(() => {});
    api.get("/owner/questions").then((r) => setQuestions(r.data || [])).catch(() => {});
    api.get("/owner/reviews").then((r) => setReviews(r.data || [])).catch(() => {});
  };

  useEffect(() => {
    if (!localStorage.getItem("bh_owner_token")) {
      navigate("/owner/login");
      return;
    }
    api.get("/owner/me").then((r) => setMe(r.data)).catch(() => {
      localStorage.removeItem("bh_owner_token");
      navigate("/owner/login");
    });
    api.get("/categories").then((r) => setCategories(r.data || [])).catch(() => {});
    api.get("/settings/public").then((r) => setAutoPublish(!!r.data.owner_auto_publish)).catch(() => {});
    load();
  }, [navigate]);

  const logout = () => {
    localStorage.removeItem("bh_owner_token");
    navigate("/owner/login");
  };

  const openEdit = (l) => {
    setForm({
      ...EMPTY, ...l,
      images: (l.images || []).join("\n"),
      languages: (l.languages || []).join(", "),
      hours_struct: parseHoursToStruct(l.hours || {}),
      address: { line1: "", city: "", state: "", postal: "", country: "US", ...(l.address || {}) },
      actions: l.actions || {},
    });
    setEditing(l.id);
  };

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    const payload = {
      ...form,
      hours: structToHours(form.hours_struct),
      images: form.images.split("\n").map((s) => s.trim()).filter(Boolean),
      languages: form.languages.split(",").map((s) => s.trim()).filter(Boolean),
      actions: Object.fromEntries(Object.entries(form.actions || {}).filter(([, v]) => v)),
    };
    delete payload.hours_struct;
    delete payload.id; delete payload.slug; delete payload.views; delete payload.status;
    delete payload.featured; delete payload.sponsored; delete payload.verified;
    delete payload.created_at; delete payload.updated_at; delete payload.reviews; delete payload.rating_avg; delete payload.questions;
    try {
      if (editing === "new") {
        await api.post("/owner/listings", payload);
        toast.success(t("toast.own.new"));
      } else {
        const { data } = await api.put(`/owner/listings/${editing}`, payload);
        toast.success(data.status === "published" ? t("toast.own.pub") : t("toast.own.pend"));
      }
      setEditing(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const uploadPhotos = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await api.post("/uploads", fd, {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${localStorage.getItem("bh_owner_token")}`,
          },
        });
        setForm((prev) => ({ ...prev, images: prev.images ? `${prev.images}\n${data.url}` : data.url }));
      }
      toast.success(files.length > 1 ? `${files.length} ${t("toast.own.photos")}` : t("toast.own.photo"));
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const uploadLogo = async (e) => {
    const f = (e.target.files || [])[0];
    if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/uploads", fd, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${localStorage.getItem("bh_owner_token")}`,
        },
      });
      setForm((prev) => ({ ...prev, logo: data.url }));
      toast.success(t("toast.own.logo"));
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const removeImage = (url) =>
    setForm((prev) => ({ ...prev, images: prev.images.split("\n").filter((x) => x.trim() && x.trim() !== url).join("\n") }));

  const replyReview = async (rid) => {
    const reply = (replies[rid] || "").trim();
    if (!reply) return;
    try {
      await api.put(`/owner/reviews/${rid}/reply`, { reply });
      toast.success(t("toast.own.reply"));
      setReplies((r) => ({ ...r, [rid]: "" }));
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const answerQuestion = async (qid) => {
    const answer = (answers[qid] || "").trim();
    if (!answer) return;
    try {
      await api.put(`/owner/questions/${qid}/answer`, { answer });
      toast.success(t("toast.own.answer"));
      setAnswers((a) => ({ ...a, [qid]: "" }));
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setAddr = (k, v) => setForm((f) => ({ ...f, address: { ...f.address, [k]: v } }));
  const totalViews = (listings || []).reduce((n, l) => n + (l.views || 0), 0);
  const unanswered = questions.filter((q) => !q.answer);

  if (!me) return <div className="min-h-screen bg-sand flex items-center justify-center"><div className="w-10 h-10 rounded-full border-2 border-gold border-t-transparent animate-spin" /></div>;

  return (
    <div className="min-h-screen bg-sand/60" data-testid="owner-dashboard">
      <header className="bg-teak text-cream border-b border-teak-surface" data-testid="owner-header">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <a href="/" className="flex items-center gap-2.5 hover:opacity-90 transition-opacity" data-testid="owner-brand-link">
            <img src="/bh-icon-180.png" alt="BurmeseHub logo" className="w-8 h-8 rounded-full" />
            <p className="font-display font-extrabold text-sm">BURMESE <span className="text-gold">HUB</span>
              <span className="ml-2 text-[10px] font-mono uppercase tracking-[0.2em] text-cream/40">{t("own.portal")}</span>
            </p>
          </a>
          <div className="flex items-center gap-4">
            <div className="flex items-center rounded-full border border-teak-line bg-teak-surface p-0.5" data-testid="lang-toggle">
              <button
                onClick={() => setLang("en")}
                data-testid="lang-toggle-en"
                className={`px-2.5 py-1 text-[11px] font-bold rounded-full transition-colors duration-200 ${lang === "en" ? "bg-gold text-ink" : "text-cream/50 hover:text-cream"}`}
              >
                EN
              </button>
              <button
                onClick={() => setLang("my")}
                data-testid="lang-toggle-my"
                style={{ fontFamily: "'Noto Sans Myanmar', sans-serif" }}
                className={`px-2.5 py-1 text-[11px] font-bold rounded-full transition-colors duration-200 ${lang === "my" ? "bg-gold text-ink" : "text-cream/50 hover:text-cream"}`}
              >
                မြန်မာ
              </button>
            </div>
            <span className="text-xs text-cream/50 font-mono hidden sm:block">{me.email}</span>
            <button onClick={logout} className="flex items-center gap-2 text-xs text-cream/60 hover:text-cream" data-testid="owner-logout-btn">
              <LogOut className="w-4 h-4" /> {t("own.signout")}
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8 space-y-8">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <h1 className="font-display text-2xl sm:text-3xl font-bold text-ink" data-testid="owner-welcome">{t("own.welcome")}, {me.name}</h1>
            <p className="text-sm text-stone2 mt-1 flex items-center gap-2">
              {autoPublish ? (
                <><CheckCircle2 className="w-4 h-4 text-jade" /> {t("own.autopub")}</>
              ) : (
                <><Clock3 className="w-4 h-4 text-gold-dark" /> {t("own.reviewnote")}</>
              )}
            </p>
          </div>
          <button onClick={() => { setForm({ ...EMPTY, category: categories[0]?.slug || "" }); setEditing("new"); }} className="inline-flex items-center gap-2 bg-ruby text-cream text-sm font-semibold px-5 py-2.5 rounded-xl" data-testid="owner-new-listing-btn">
            <Plus className="w-4 h-4" /> {t("own.newlisting")}
          </button>
        </div>

        <div className="grid grid-cols-3 gap-4">
          {[
            { icon: Store, labelKey: "own.stat.listings", value: listings?.length ?? 0, tid: "owner-stat-listings" },
            { icon: Eye, labelKey: "own.stat.views", value: totalViews, tid: "owner-stat-views" },
            { icon: MessageCircleQuestion, labelKey: "own.stat.questions", value: unanswered.length, tid: "owner-stat-questions" },
          ].map((c) => (
            <div key={c.tid} className="bg-white p-5 rounded-xl border border-[#E6DED3] shadow-sm" data-testid={c.tid}>
              <c.icon className="w-5 h-5 text-ruby mb-3" />
              <p className="font-display text-2xl font-bold text-ink">{c.value}</p>
              <p className="text-xs text-stone2 mt-1">{t(c.labelKey)}</p>
            </div>
          ))}
        </div>

        <div className="bg-white rounded-xl border border-[#E6DED3] shadow-sm overflow-x-auto">
          <table className="w-full text-left min-w-[640px]" data-testid="owner-listings-table">
            <thead className="border-b border-sand">
              <tr><th className={thCls}>{t("own.th.listing")}</th><th className={thCls}>{t("own.th.category")}</th><th className={thCls}>{t("own.th.status")}</th><th className={thCls}>{t("own.th.views")}</th><th className={thCls}>{t("own.th.actions")}</th></tr>
            </thead>
            <tbody className="divide-y divide-sand">
              {(listings || []).map((l) => (
                <tr key={l.id} data-testid={`owner-listing-row-${l.id}`}>
                  <td className={`${tdCls} font-semibold`}>{l.name}</td>
                  <td className={tdCls}>{tOr(`cat.${l.category}`, l.category_label)}</td>
                  <td className={tdCls}><StatusBadge status={l.status} label={tOr(`status.${l.status}`)} /></td>
                  <td className={tdCls}>{l.views || 0}</td>
                  <td className={tdCls}>
                    <button onClick={() => openEdit(l)} className="p-2 text-clay hover:bg-sand rounded-lg" data-testid={`owner-edit-${l.id}`}><Pencil className="w-4 h-4" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {listings && listings.length === 0 && (
            <p className="px-5 py-10 text-sm text-stone2 text-center">
              {t("own.empty")}
            </p>
          )}
        </div>

        {questions.length > 0 && (
          <div className="space-y-4" data-testid="owner-questions">
            <h2 className="font-display text-xl font-bold text-ink">{t("own.questions")}</h2>
            {questions.map((q) => (
              <div key={q.id} className="bg-white rounded-xl border border-[#E6DED3] p-5" data-testid={`owner-question-${q.id}`}>
                <p className="text-sm font-semibold text-ink">{q.text}</p>
                <p className="text-xs text-stone2 mt-1">{q.listing_name} · {t("own.askedby")} {q.name} · {(q.created_at || "").slice(0, 10)}</p>
                {q.answer ? (
                  <div className="mt-3 pl-4 border-l-2 border-gold">
                    <p className="text-sm text-clay">{q.answer}</p>
                    <p className="text-[10px] uppercase tracking-wider text-jade font-semibold mt-1">{t("own.answered")}</p>
                  </div>
                ) : (
                  <div className="mt-3 flex flex-col sm:flex-row gap-2">
                    <input
                      value={answers[q.id] || ""}
                      onChange={(e) => setAnswers((a) => ({ ...a, [q.id]: e.target.value }))}
                      placeholder={t("own.answerph")}
                      className={inputCls}
                      data-testid={`answer-input-${q.id}`}
                    />
                    <button onClick={() => answerQuestion(q.id)} className="bg-ruby hover:bg-ruby-dark text-cream text-xs font-bold px-5 py-2.5 rounded-xl shrink-0" data-testid={`answer-btn-${q.id}`}>
                      {t("own.publishanswer")}
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {reviews.length > 0 && (
          <div className="space-y-4" data-testid="owner-reviews">
            <h2 className="font-display text-xl font-bold text-ink flex items-center gap-2"><MessageSquare className="w-5 h-5 text-ruby" /> {t("own.reviews")}</h2>
            {reviews.map((r) => (
              <div key={r.id} className="bg-white rounded-xl border border-[#E6DED3] p-5" data-testid={`owner-review-${r.id}`}>
                <div className="flex items-center justify-between mb-1.5">
                  <p className="text-sm font-semibold text-ink">{r.name} <span className="font-normal text-xs text-stone2">· {r.listing_name} · {(r.created_at || "").slice(0, 10)}</span></p>
                  <span className="flex gap-0.5">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className={`w-3.5 h-3.5 ${i < r.rating ? "fill-gold text-gold" : "text-sand"}`} />
                    ))}
                  </span>
                </div>
                <p className="text-sm text-clay leading-relaxed">{r.text}</p>
                {r.reply ? (
                  <div className="mt-3 pl-4 border-l-2 border-gold">
                    <p className="text-sm text-clay">{r.reply}</p>
                    <p className="text-[10px] uppercase tracking-wider text-jade font-semibold mt-1">{t("own.replied")}</p>
                  </div>
                ) : (
                  <div className="mt-3 flex flex-col sm:flex-row gap-2">
                    <input
                      value={replies[r.id] || ""}
                      onChange={(e) => setReplies((x) => ({ ...x, [r.id]: e.target.value }))}
                      placeholder={t("own.replyph")}
                      className={inputCls}
                      data-testid={`reply-input-${r.id}`}
                    />
                    <button onClick={() => replyReview(r.id)} className="bg-ink hover:bg-ruby text-cream text-xs font-bold px-5 py-2.5 rounded-xl shrink-0" data-testid={`reply-btn-${r.id}`}>
                      {t("own.publishreply")}
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {analytics.length > 0 && (
          <div className="space-y-4" data-testid="owner-analytics">
            <h2 className="font-display text-xl font-bold text-ink">{t("own.analytics")}</h2>
            {analytics.map((a) => {
              const maxV = Math.max(...a.daily.map((x) => x.views), 1);
              return (
                <div key={a.id} className="bg-white rounded-xl border border-[#E6DED3] p-5" data-testid={`analytics-${a.id}`}>
                  <p className="font-semibold text-ink mb-3">{a.name}</p>
                  <div className="grid grid-cols-3 sm:grid-cols-6 gap-3 mb-4">
                    {["view", "website", "phone", "directions", "share", "save"].map((k) => (
                      <div key={k} className="text-center bg-sand/50 rounded-lg py-2.5">
                        <p className="font-display text-lg font-bold text-ink">{a.counts[k]}</p>
                        <p className="text-[10px] uppercase tracking-wider text-stone2">{t(`own.m.${k}`)}</p>
                      </div>
                    ))}
                  </div>
                  <div className="flex items-end gap-1.5 h-16" data-testid={`analytics-chart-${a.id}`}>
                    {a.daily.map((d) => (
                      <div key={d.day} className="flex-1 flex flex-col items-center justify-end gap-1 h-full">
                        <div className="w-full bg-gold/80 rounded-t transition-all" style={{ height: `${Math.max((d.views / maxV) * 100, 5)}%` }} title={`${d.day}: ${d.views} views`} />
                        <span className="text-[9px] text-stone2">{d.day.slice(5)}</span>
                      </div>
                    ))}
                  </div>
                  <p className="text-[10px] text-stone2 mt-1.5">{t("own.chartnote")}</p>
                </div>
              );
            })}
          </div>
        )}
      </main>

      {editing && (
        <Modal title={editing === "new" ? t("own.modal.new") : `${t("own.modal.edit")} — ${form.name}`} onClose={() => setEditing(null)} wide>
          <form onSubmit={save} className="space-y-4" data-testid="owner-listing-form">
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label={t("own.f.name")}><input required value={form.name} onChange={(e) => set("name", e.target.value)} className={inputCls} disabled={editing !== "new"} data-testid="owner-edit-name" /></Field>
              <Field label={t("own.f.category")}>
                <select required value={form.category} onChange={(e) => set("category", e.target.value)} className={inputCls} disabled={editing !== "new"} data-testid="owner-edit-category">
                  {categories.map((c) => <option key={c.slug} value={c.slug}>{tOr(`cat.${c.slug}`, c.label)}</option>)}
                </select>
              </Field>
            </div>
            <Field label={t("own.f.description")}><textarea required rows={3} value={form.description} onChange={(e) => set("description", e.target.value)} className={inputCls} data-testid="owner-edit-description" /></Field>
            <div className="grid sm:grid-cols-2 gap-4">
              {form.category !== "restaurants" && (
                <Field label={t("own.f.price")}><input value={form.price} onChange={(e) => set("price", e.target.value)} className={inputCls} data-testid="owner-edit-price" /></Field>
              )}
              <Field label={t("own.f.eventdate")}><input type="date" value={form.event_date || ""} onChange={(e) => set("event_date", e.target.value)} className={inputCls} data-testid="owner-edit-event-date" /></Field>
              <Field label={t("own.f.phone")}><input value={form.phone} onChange={(e) => set("phone", e.target.value)} className={inputCls} data-testid="owner-edit-phone" /></Field>
              <Field label={t("own.f.website")}><input value={form.website} onChange={(e) => set("website", e.target.value)} className={inputCls} data-testid="owner-edit-website" /></Field>
              <Field label={t("own.f.city")}><input value={form.address.city} onChange={(e) => setAddr("city", e.target.value)} className={inputCls} data-testid="owner-edit-city" /></Field>
              <Field label={t("own.f.state")}><input value={form.address.state} onChange={(e) => setAddr("state", e.target.value)} className={inputCls} data-testid="owner-edit-state" /></Field>
            </div>
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-stone2 mb-1.5">{t("own.f.photos")}</p>
              <label className={`flex items-center justify-center gap-2 w-full py-4 rounded-xl border-2 border-dashed border-gold/50 bg-gold-light/40 text-sm font-semibold text-gold-dark cursor-pointer hover:bg-gold-light transition-colors ${uploading ? "opacity-60 pointer-events-none" : ""}`} data-testid="owner-upload-photos">
                <ImagePlus className="w-5 h-5" />
                {uploading ? t("own.f.uploading") : t("own.f.upload")}
                <input type="file" accept="image/*" multiple capture="environment" className="hidden" onChange={uploadPhotos} data-testid="owner-file-input" />
              </label>
              {form.images.trim() && (
                <div className="flex flex-wrap gap-2 mt-3" data-testid="owner-photo-thumbs">
                  {form.images.split("\n").map((s) => s.trim()).filter(Boolean).map((url) => (
                    <div key={url} className="relative w-20 h-16 rounded-lg overflow-hidden border border-[#E6DED3] group">
                      <img src={url} alt="" className="w-full h-full object-cover" />
                      <button type="button" onClick={() => removeImage(url)} className="absolute top-0.5 right-0.5 w-5 h-5 bg-ink/70 text-cream rounded-full flex items-center justify-center" data-testid={`remove-photo-${url.slice(-8)}`}>
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-stone2 mb-1.5">{t("own.f.logo")}</p>
              <div className="flex items-center gap-4">
                {form.logo ? (
                  <div className="relative w-20 h-20 rounded-xl overflow-hidden border border-[#E2E8F0] bg-white shrink-0" data-testid="owner-logo-preview">
                    <img src={form.logo} alt="Business logo" className="w-full h-full object-cover" />
                    <button type="button" onClick={() => set("logo", "")} className="absolute top-1 right-1 w-5 h-5 bg-ink/70 text-cream rounded-full flex items-center justify-center" data-testid="owner-logo-remove">
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ) : (
                  <label className={`flex items-center justify-center gap-2 w-40 h-20 rounded-xl border-2 border-dashed border-gold/50 bg-gold-light/40 text-xs font-semibold text-gold-dark cursor-pointer hover:bg-gold-light transition-colors ${uploading ? "opacity-60 pointer-events-none" : ""}`} data-testid="owner-upload-logo">
                    <ImagePlus className="w-4 h-4" />
                    {uploading ? t("own.f.uploading") : t("own.f.logoupload")}
                    <input type="file" accept="image/*" className="hidden" onChange={uploadLogo} data-testid="owner-logo-input" />
                  </label>
                )}
              </div>
            </div>
            <Field label={t("ld.hours")}>
              <HoursEditor value={form.hours_struct} onChange={(v) => set("hours_struct", v)} />
            </Field>
            <div className="grid sm:grid-cols-2 gap-4">
              {["order_online", "menu", "book", "reservation", "quote", "shop"].map((k) => (
                <Field key={k} label={t(`own.a.${k}`)}>
                  <input value={form.actions?.[k] || ""} onChange={(e) => setForm((f) => ({ ...f, actions: { ...f.actions, [k]: e.target.value } }))} className={inputCls} data-testid={`owner-edit-action-${k}`} />
                </Field>
              ))}
            </div>
            <button type="submit" disabled={busy} className="w-full bg-ruby hover:bg-ruby-dark text-cream font-bold py-3.5 rounded-xl disabled:opacity-60" data-testid="owner-save-btn">
              {busy ? t("own.saving") : autoPublish || editing === "new" ? t("own.save") : t("own.saveReview")}
            </button>
          </form>
        </Modal>
      )}
    </div>
  );
}
